import React, { useEffect, useState } from 'react';
import { Search as SearchIcon, X, Clock, Trash2 } from 'lucide-react';
import { search, getList, endpoints } from '../api/tmdb';
import MovieCard from '../components/MovieCard';
import { fetchSearchHistory, pushSearchHistory, clearSearchHistory } from '../api/services';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { filterForKids } from '../utils/kidsFilter';

const SearchPage = () => {
  const { user } = useAuth();
  const { profile } = useApp();
  const isKids = !!profile?.is_kids;
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [trending, setTrending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getList(endpoints.trending('all', 'week')).then((d) => setTrending(filterForKids(d, isKids)));
  }, [isKids]);

  useEffect(() => {
    if (user) fetchSearchHistory().then(setHistory).catch(() => setHistory([]));
  }, [user]);

  useEffect(() => {
    const q2 = q.trim();
    if (!q2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await search(q2);
        setResults(filterForKids(r, isKids));
        if (user) {
          pushSearchHistory(q2).then(() => fetchSearchHistory().then(setHistory)).catch(() => {});
        }
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [q, user, isKids]);

  const clearAll = async () => {
    if (!user) return;
    await clearSearchHistory();
    setHistory([]);
  };

  return (
    <div className="min-h-screen pt-16 px-10 pb-20">
      <div className="max-w-4xl">
        <h1 className="font-display text-5xl md:text-6xl text-white">Search</h1>
        <p className="text-white/60 mt-2">Find movies, shows, actors and more.</p>

        <div className="mt-8 relative">
          <SearchIcon className="absolute left-5 top-1/2 -translate-y-1/2 text-white/50" size={22} />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Try 'Stranger Things' or 'Dune'"
            className="w-full pl-14 pr-14 py-5 text-lg bg-white/8 border border-white/15 rounded-2xl text-white placeholder:text-white/40 focus:outline-none focus:border-white/50 focus:bg-white/12"
          />
          {q && (
            <button onClick={() => setQ('')} className="absolute right-5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white">
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {!q.trim() && history.length > 0 && (
        <div className="mt-6 max-w-4xl">
          <div className="flex items-center justify-between">
            <div className="text-white/60 text-sm flex items-center gap-2"><Clock size={14} /> Recent searches</div>
            <button onClick={clearAll} className="text-white/50 hover:text-white text-xs flex items-center gap-1"><Trash2 size={12} /> Clear</button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {history.map((h) => (
              <button key={h.query} onClick={() => setQ(h.query)} className="px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/80 hover:bg-white/15 text-sm">
                {h.query}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-12">
        {q.trim() ? (
          <>
            <h2 className="text-xl font-semibold mb-6">
              {loading ? 'Searching…' : results.length ? `Results for “${q}”` : `No results for “${q}”`}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
              {results.map((r) => (
                <MovieCard key={`${r.media_type}-${r.id}`} item={r} />
              ))}
            </div>
          </>
        ) : (
          <>
            <h2 className="text-xl font-semibold mb-6">Trending this week</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-5">
              {trending.map((r) => (
                <MovieCard key={`${r.media_type || 'm'}-${r.id}`} item={r} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
